import { Box, Button, Grid, Stack, Tooltip } from '@mui/material';
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import useApi from 'hooks/useApi';
import { useSelector } from 'store/hooks';
import Order from 'types/entities/Order';
import User from 'types/entities/User';
import { loadDeliveryManagersAction } from 'store/actions/changeBaselineActions';
import { injectOrderAction, injectSelectedWorkunitsAction } from 'store/actions/catalogToOrderActions';
import OrderApiService from 'api/OrderApiService';
import { Layout, StepperHeader } from 'components';
import { OrderWorkunitAffectationProvider } from 'features/Affectation/__context/OrderWorkunitAffectationContext';
import { OrderWorkunitsAffectation, OrderWorkunitsAffectationRefType } from 'features/Affectation/Affectation';
import EngagementParameters from './QuotesPages/EngagementParameters/EngagementParameters';
import { useAppState } from 'store/Provider';
import { setSnackbarAction } from 'store/actions/snackbarActions';
import Scope from 'types/entities/Scope';
import UserApiService from 'api/UserApiService';
import moment from 'moment';
import { getPurchaseOrderAction } from 'store/actions/purchaseOrderActions';
import { formatDateToddMMYYY } from 'helpers/format';

import OrderWorkunits from 'features/Orders/OrderWorkunits/OrderWorkunits';
import useOrderValidation from 'features/Orders/OrderWorkunits/services/useOrderValidation';
import ExtractExcelDialogs from 'features/Orders/OrderWorkunits/OrderWorkunitsDataGrids/SummaryDataGrid/ExtractExcelDialogs';
import OrderWorkunitsSummary, { OrderWorkunitsSummaryRefType } from 'features/Orders/OrderWorkunitsSummary';
import useManagementService from '../Management/services/useManagementService';
import ManagementDrawers from '../Management/ManagementDrawers';

export interface ChildRefType {
  handleSubmitEngagementPara: () => Promise<void>;
}

export interface ChildRefBLLastpageType {
  handleSubmitBLSummary: (fname: string) => void;
}

interface CustomStep {
  id: number;
  title: string;
  subtitle: string;
}

export interface QuotesLocationState {
  initialStep?: number;
  order?: Order;
}

const Quotes: React.FC = () => {
  const { catalog_id, order_id } = useParams();

  const location = useLocation();
  const { makeCall } = useApi();
  const { dispatch } = useAppState();
  const [t] = useTranslation();
  const history = useHistory();
  const { validateOrder } = useManagementService();
  // Steps 2 -> 3 stay inside the wizard: "don't extract"/"extract" should advance to the
  // Affectation step instead of navigating away to the Orders "Quotes" tab.
  const handleNextRef = useRef<(() => void) | null>(null);
  const handleWizardRedirectToPreview = useCallback(() => {
    handleNextRef.current?.();
  }, []);
  const {
    order: catalogOrder,
    isExtractModalOpen,
    isReferencesModalOpen,
    isExtractLoading,
    specRef,
    setSpecRef,
    chronoRef,
    setChronoRef,
    referencesError,
    handleValidationOrder,
    handleExtractModalClose,
    handleOpenReferencesModal,
    handleReferencesModalClose,
    downloadExtractAndRedirectWithReferences,
    redirectToPreview,
    hasUnsavedWorkunitChanges,
  } = useOrderValidation(handleWizardRedirectToPreview);
  const EngagementParametersRef = useRef<ChildRefType>(null);
  const OrderWorkunitsSummaryRef = useRef<OrderWorkunitsSummaryRefType>(null);
  const OrderWorkunitsAffectationRef = useRef<OrderWorkunitsAffectationRefType>(null);
  // Set right before handleSubmitEngagementPara() when a step tab (not Save & Next) triggers
  // step 1's save, so the handleNext() it fires internally on success lands on the tab the
  // user actually clicked instead of always advancing by one step. Cleared once handleNext
  // consumes it, and defensively cleared again once the save settles so a failed/skipped
  // save can't leave a stale target step to hijack a later, unrelated handleNext() call.
  const targetStepRef = useRef<number | null>(null);
  const appState = useSelector((state) => state.app);
  const loadings = useSelector((state) => state.loadings);
  const liveEditOrder = useSelector((state) => state.catalogToOrder?.order);
  const management = useSelector((state) => state.management);
  // sourceBaseOrder.final_amount_price only refreshes on fetch/save, so it's an unreliable
  // signal for gating Save/Next on step 2. Use the live selectedWorkunits count instead -
  // it mirrors the workunits grid's own noRowsOverlay condition.
  const selectedWorkunits = useSelector((state) => state.catalogToOrder.selectedWorkunits);

  // Lets callers (e.g. the pending-order "Go_ahead" action) deep-link straight into a
  // wizard step, bypassing Engagement Parameters/Workunits when the order already has that data.
  const navigationState = location.state as QuotesLocationState | undefined;
  const initialStep = navigationState?.initialStep ?? 1;
  // The order list already has the full order (affair, dates, etc.) in memory - seed
  // sourceBaseOrder with it so a deep-link to step 3+ doesn't show empty fields while
  // fetchOrder (which only runs for currentStep === 1) hasn't populated it yet.
  const navigationOrder = navigationState?.order;

  // State
  const [activeBaseFlag, setActiveBaseFlag] = useState(initialStep === 1 ? 'EngagementPage' : '');
  const [activeStep, setActiveStep] = useState(initialStep - 1);
  const [completed, setCompleted] = useState<{ [k: number]: boolean }>(() => {
    const initialCompleted: { [k: number]: boolean } = {};
    for (let stepId = 1; stepId < initialStep; stepId += 1) {
      initialCompleted[stepId] = true;
    }
    return initialCompleted;
  });
  const [sourceBaseOrder, setSourceBaseOrder] = useState<Partial<Order>>({
    start_date: moment().format('YYYY-MM-DD'), // moment().toISOString(),
    end_date: moment().format('YYYY-MM-DD'), // moment().toISOString(),
    is_sign_mandatory: true,
    client_connection: appState.customer?.client_connection,
    name_spec: '',
    ...navigationOrder,
  });
  const [selectedConsultants, setSelectedConsultants] = useState<User[]>([]);
  const [deliveryManagers, setDeliveryManagers] = useState<User[]>([]);
  const [representatives, setRepresentatives] = useState<User[]>([]);
  const [selectedDeliveryManager, setSelectedDeliveryManager] = useState<User[]>([]);
  const [selectedRepresentative, setSelectedRepresentative] = useState<User[]>([]);
  const [selectedScopes, setSelectedScopes] = useState<any[]>([]);
  const [availableScopes, setAvailableScopes] = useState<Scope[]>([]);
  const [availableConsultants, setAvailableConsultants] = useState<User[]>([]);
  const [availableDeliveryManagers, setAvailableDeliveryManagers] = useState<User[]>([]);
  const [, setAvailableRepresentatives] = useState<User[]>([]);
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [isMissingInputs, setIsMissingInputs] = useState(false); // Validation

  // Prevent multiple API calls
  const [isCreating, setIsCreating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAffectationSaving, setIsAffectationSaving] = useState(false);
  // True for the whole duration of a step-tab click (handleStepClick): saving the step being
  // left, then switching. Distinct from isCreating (also used by the Save/Save-as-draft
  // buttons on the *current* step) so clicking a tab shows a full loading state on every
  // step (1-4) instead of leaving stale content visible while the save/transition runs.
  const [isStepChanging, setIsStepChanging] = useState(false);
  const isMountedRef = useRef(true);

  // Falls back to the order created in step 1 when the URL has no :order_id
  // (route /orders/:catalog_id/new-order), so Back/fetchOrder still work for a brand-new order.
  const effectiveOrderId = useMemo(
    () => order_id || (sourceBaseOrder?.id ? String(sourceBaseOrder.id) : ''),
    [order_id, sourceBaseOrder?.id]
  );

  const totalSteps = 4;

  // Production orders are already fully processed: steps 2-4 become a read-only
  // walkthrough - Save as Draft is disabled, and Save & Next only navigates
  // (no save/PUT/POST calls), relabeled "Next".
  const isProductionOrder = sourceBaseOrder?.status === 'production';

  // Step components
  const steps: CustomStep[] = [
    { id: 1, title: t('Engagement_Parameters'), subtitle: t('Define_Parameters') },
    { id: 2, title: t('Workunits'), subtitle: t('Select_Workunits') },
    { id: 3, title: t('Affectation'), subtitle: t('Affectation_Subtitle') },
    { id: 4, title: t('Order_Summary'), subtitle: t('Order_Summary_Subtitle') },
  ];

  useEffect(() => {
    const getDeliveryManagers = async () => {
      try {
        const res = await UserApiService.fetchPossiblesDeliverableTeamLeaders();

        const sortedRes = res.sort((a, b) => {
          const nameA = a.last_name.toUpperCase();
          const nameB = b.last_name.toUpperCase();
          if (nameA < nameB) return -1;
          if (nameA > nameB) return 1;
          return 0;
        });

        dispatch(loadDeliveryManagersAction(sortedRes));
      } catch (error) {
        console.error('Failed to fetch saved order:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (appState.customer?.id) getDeliveryManagers();
  }, [appState.customer?.id]);

  // Memoized functions to prevent re-renders
  const getPurchaseOrder = useCallback(
    async (order: Order) => {
      if (!order?.id || !order?.affair?.otp) return;
      // setIsLoading(true);
      try {
        dispatch(getPurchaseOrderAction([]));
        const purchaseOrderList = await makeCall(
          OrderApiService.fetchEditPurchaseOrder(order.id, order.affair.otp as string)
        );

        if (purchaseOrderList.length > 0) {
          purchaseOrderList.forEach(
            (po: {
              poend: string;
              poref: string;
              reference: string;
              postart: string;
              is_valid: boolean;
              is_archived: boolean;
            }) => {
              po.reference = `${po.poref} (${formatDateToddMMYYY(po.postart)} - ${formatDateToddMMYYY(po.poend)})`;
            }
          );
        }

        if (isMountedRef.current) {
          dispatch(getPurchaseOrderAction(purchaseOrderList));
        }
        // setIsLoading(false);
      } catch (error) {
        console.error('Failed to fetch purchase orders:', error);
        // setIsLoading(false);
      }
    },
    [makeCall, dispatch]
  );

  const fetchOrder = useCallback(async () => {
    if (isLoading || !effectiveOrderId || !appState?.customer?.id) {
      return;
    }
    setIsLoading(false);
    setActiveBaseFlag('EngagementPage');

    try {
      const { orderForCustomer } = await makeCall(
        OrderApiService.fetchCurrentOrdersListManagement(appState.customer.id, {
          id: Number(effectiveOrderId),
          size: 1,
        })
      );

      if (!isMountedRef.current) {
        // Component unmounted while the request was in flight - don't touch state or navigate.
        return;
      }

      if (orderForCustomer?.[0]) {
        const order = orderForCustomer[0];
        dispatch(injectOrderAction(order));
        setSelectedScopes(order.tmscopes || []);
        await getPurchaseOrder(order);
        setSelectedConsultants(order.tmconsultants || []);
        setSelectedDeliveryManager(order.tmaccountables || []);
        setSelectedRepresentative(order.tmdelegues || []);
        setAvailableScopes(order.scopes || []);
        setAvailableConsultants(order.consultants || []);
        setAvailableDeliveryManagers(order.delivery_managers || []);
        setAvailableRepresentatives(order.representatives || []);
        setSourceBaseOrder(order);
        setIsLoading(false);
      } else {
        // Order genuinely not found - return to the orders list.
        history.push(`/${appState.customer?.slug}/orders/production?status=production`);
      }
    } catch (error) {
      console.error(' Failed to fetch order:', error);
      setIsLoading(false);
      if (isMountedRef.current) {
        dispatch(
          setSnackbarAction({
            message: `Failed to fetch order: ${error}`,
            open: true,
            severity: 'error',
          })
        );
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [
    effectiveOrderId,
    appState?.customer?.id,
    appState?.customer?.slug,
    makeCall,
    dispatch,
    getPurchaseOrder,
  ]);

  // Initial fetch effect
  useEffect(() => {
    if (activeBaseFlag === 'EngagementPage' && currentStep === 1 && effectiveOrderId) {
      fetchOrder();
    }
  }, [effectiveOrderId, currentStep, fetchOrder, activeBaseFlag]);

  // Track mount state so the async fetchOrder never acts on an unmounted component.
  // The ref MUST be set true on (re)mount, not only false on cleanup: React 18/19
  // StrictMode double-invokes effects in dev (mount -> unmount -> remount), and without
  // this reset the ref stayed false after the remount, sending fetchOrder down its
  // "not found" branch and bouncing the edit wizard straight back to the orders list.
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Auto-advances from step 3 to step 4 once the order being affected is replaced by a new
  // object in the orders list (i.e. it was actually re-saved elsewhere). Tracks the matched
  // order's own object identity rather than "have we seen management.orders at all" - a
  // boolean flag breaks under StrictMode, which double-invokes this effect on mount with the
  // exact same (unchanged) management.orders: the first invocation would flip the flag and
  // the second, forced invocation would then wrongly fire immediately. Comparing object
  // identity is stable across that double-invoke since the array/order reference is identical,
  // while a deep link straight to step 3 (e.g. the "Go_Ahead" action) also starts pre-seeded
  // with the matching order so it isn't mistaken for a fresh update either.
  const lastMatchedOrderRef = useRef(management?.orders?.find((order) => order.id === sourceBaseOrder.id));
  useEffect(() => {
    if (currentStep === 3) {
      const matchedOrder = management?.orders?.find((order) => order.id === sourceBaseOrder.id);
      if (matchedOrder && matchedOrder !== lastMatchedOrderRef.current) {
        lastMatchedOrderRef.current = matchedOrder;
        handleOrderCreated(matchedOrder);
      }
    }
  }, [management.orders]);

  const handleSave = useCallback(async () => {
    if (currentStep === 1) {
      // Call child function from parent
      if (EngagementParametersRef.current) {
        await EngagementParametersRef.current.handleSubmitEngagementPara();
      }
    } else if (currentStep === 2) {
      if (isProductionOrder) {
        handleNext();
        return;
      }
      setIsCreating(true);
      try {
        await handleValidationOrder(true);
      } finally {
        setIsCreating(false);
      }
    } else if (currentStep === 3) {
      if (isProductionOrder) {
        handleNext();
        return;
      }
      if (sourceBaseOrder?.client_id) {
        setIsCreating(true);
        try {
          await OrderWorkunitsAffectationRef.current?.validateOrderStatus();
          handleNext();
        } finally {
          setIsCreating(false);
        }
      } else {
        await validateOrder(sourceBaseOrder as Order);
      }
    } else if (currentStep === 4) {
      // if (isProductionOrder) {
      //   history.replace(`/${appState.customer?.slug}/orders/production?status=production`);
      //   return;
      // }
      if (OrderWorkunitsSummaryRef.current) {
        setIsCreating(true);
        try {
          const success = await OrderWorkunitsSummaryRef.current.handleSave();
          if (success) {
            history.push(`/${appState.customer?.slug}/orders/production?status=production`, { replace: true });
          }
        } finally {
          setIsCreating(false);
        }
      }
    }
  }, [currentStep, handleValidationOrder, history, appState.customer?.slug, isProductionOrder, management.orders]);

  const handleStepClick = useCallback(
    async (stepId: number) => {
      if (stepId === currentStep) return;
      // The stepper header isn't visually disabled while a step's own Save & Next is
      // mid-save (isCreating) or Affectation is saving (isAffectationSaving) - only
      // isLoading gates the `disable` prop below. Without this guard, clicking another
      // step tab while a save is in flight forces currentStep to the clicked step, and
      // whichever transition resolves last "wins" - e.g. Save & Next's own handleNext()
      // landing on step 2 after a stray click on the Affectation tab already forced step 3.
      if (isCreating || isLoading || isAffectationSaving) return;

      // Covers the whole click, on every step (1-4): the save below plus the switch itself,
      // so the step content area shows a loading state instead of stale content while either
      // is in flight. See the render below (isLoading || isStepChanging).
      setIsStepChanging(true);

      // Step 1's save (handleSubmitEngagementPara) is async and advances the wizard itself
      // via its own handleNext() call once the order is created/updated. That handleNext
      // resolves after this function returns and would stomp any target step we set here,
      // so step 1 fully owns its own navigation - same as the Save button. Record the
      // clicked tab first so handleNext lands there instead of just going to step 2.
      if (currentStep === 1) {
        targetStepRef.current = stepId;
        EngagementParametersRef.current?.handleSubmitEngagementPara().finally(() => {
          targetStepRef.current = null;
          setIsStepChanging(false);
        });
        return;
      }

      try {
        // Persist the current step's data before navigating away, same as the Save button
        if (currentStep === 2) {
          setIsCreating(true);
          try {
            await handleValidationOrder(true);
          } finally {
            setIsCreating(false);
          }
        } else if (currentStep === 4 && OrderWorkunitsSummaryRef.current) {
          setIsCreating(true);
          try {
            await OrderWorkunitsSummaryRef.current.saveAsDraft();
          } finally {
            setIsCreating(false);
          }
        } else if (currentStep === 3 && !isProductionOrder) {
          // Same client/affair gate as the Save & Next button (handleSave) - clicking the
          // Order Summary tab directly must not skip past it and land on step 4 with no
          // client assigned. Stop the navigation here; validateOrder opens CompleteOrderDrawer.
          if (!sourceBaseOrder?.client_id) {
            await validateOrder(sourceBaseOrder as Order);
            return;
          }
          setIsCreating(true);
          try {
            await OrderWorkunitsAffectationRef.current?.validateOrderStatus();
          } finally {
            setIsCreating(false);
          }
        }

        if (stepId === 1) {
          setActiveBaseFlag('EngagementPage');
          if (effectiveOrderId) fetchOrder();
        } else if (stepId === 2) {
          handleExtractModalClose();
          handleReferencesModalClose();
        }

        setCurrentStep(stepId);
        setActiveStep(steps.findIndex((step) => step.id === stepId));
        dispatch(injectSelectedWorkunitsAction([]));
        setCompleted((prevCompleted) => {
          const updatedCompleted = { ...prevCompleted };
          steps.forEach((step) => {
            if (step.id < stepId) {
              updatedCompleted[step.id] = true;
            } else {
              updatedCompleted[step.id] = false;
            }
          });
          return updatedCompleted;
        });
      } finally {
        setIsStepChanging(false);
      }
    },
    [
      currentStep,
      steps,
      dispatch,
      effectiveOrderId,
      handleValidationOrder,
      fetchOrder,
      handleExtractModalClose,
      handleReferencesModalClose,
      isCreating,
      isLoading,
      isAffectationSaving,
      isProductionOrder,
      sourceBaseOrder,
      validateOrder,
    ]
  );

  const handleNext = useCallback(() => {
    if (currentStep < totalSteps) {
      setIsCreating(true);
      // A step-tab click on step 1 (handleStepClick) records the clicked tab here before
      // triggering the save, so a successful save lands on that tab instead of always
      // advancing by one step. Consume-and-clear so it only ever affects the very next
      // handleNext() call - e.g. Save & Next itself must fall back to currentStep + 1.
      const nextStep = targetStepRef.current ?? currentStep + 1;
      targetStepRef.current = null;
      // The Excel-extraction/references modals (isExtractModalOpen/isReferencesModalOpen)
      // live in useOrderValidation's state, which is scoped to the whole Quotes component,
      // not per-step - so leaving Workunits without closing them (e.g. navigating back to
      // Engagement Parameters) leaves them open in memory. handleStepClick/handleBack already
      // close them when the target step is 2; do the same here so Save & Next landing back on
      // Workunits doesn't resurrect a stale "Do you want to extract excel files?" dialog from
      // an earlier visit.
      if (nextStep === 2) {
        handleExtractModalClose();
        handleReferencesModalClose();
      }
      setCurrentStep(nextStep);
      setActiveStep(steps.findIndex((step) => step.id === nextStep));
      setCompleted((prevCompleted) => {
        const updatedCompleted = { ...prevCompleted };
        steps.forEach((step) => {
          if (step.id < nextStep) {
            updatedCompleted[step.id] = true;
          } else {
            updatedCompleted[step.id] = false;
          }
        });
        setIsCreating(false);
        dispatch(injectSelectedWorkunitsAction([]));
        return updatedCompleted;
      });
    }
  }, [currentStep, totalSteps, steps, handleExtractModalClose, handleReferencesModalClose]);

  useEffect(() => {
    handleNextRef.current = handleNext;
  }, [handleNext]);

  const handleOrderCreated = useCallback(
    (order: Order) => {
      dispatch(injectOrderAction(order));
      setSourceBaseOrder(order);
      handleNext();
    },
    [handleNext]
  );

  const handleBack = useCallback(async () => {
    if (currentStep > 1) {
      // Non-production orders always save before navigating back on steps 2-4.
      // Production orders are read-only on step 2 (workunits) - keep the old
      // production behavior there - but still save on steps 3 and 4.
      const shouldSave = currentStep !== 2 || !isProductionOrder;

      if (shouldSave) {
        setIsCreating(true);
        try {
          if (currentStep === 2) {
            if (hasUnsavedWorkunitChanges()) {
              await handleValidationOrder(true);
            }
          } else if (currentStep === 3) {
            await OrderWorkunitsAffectationRef.current?.validateOrderStatus();
          } else if (currentStep === 4 && OrderWorkunitsSummaryRef.current) {
            await OrderWorkunitsSummaryRef.current.saveAsDraft();
          }
        } finally {
          setIsCreating(false);
        }
      }
      const nextStep = currentStep - 1;
      setCurrentStep(nextStep);
      if (nextStep === 1) {
        setActiveBaseFlag('EngagementPage');
        fetchOrder();
      }
      if (nextStep === 2) {
        handleExtractModalClose();
        handleReferencesModalClose();
      }
      setActiveStep(steps.findIndex((step) => step.id === nextStep));
      dispatch(injectSelectedWorkunitsAction([]));
      setCompleted((prevCompleted) => {
        const updatedCompleted = { ...prevCompleted };
        steps.forEach((step) => {
          if (step.id < nextStep) {
            updatedCompleted[step.id] = true;
          } else {
            updatedCompleted[step.id] = false;
          }
        });
        return updatedCompleted;
      });
    }
  }, [currentStep, steps, isProductionOrder, handleValidationOrder, hasUnsavedWorkunitChanges]);

  const navigateToQuotesList = useCallback(() => {
    if (sourceBaseOrder?.status === 'production') {
      history.push(`/${appState.customer?.slug}/orders/production?status=production`);
    } else {
      history.push(`/${appState.customer?.slug}/orders/production?status=quote`);
    }
  }, [history, appState.customer?.slug]);

  // The "Cancel" button discards the quote entirely: any order already persisted
  // by an earlier step's Save must be archived, not just navigated away from.
  const handleCancel = useCallback(async () => {
    navigateToQuotesList();
  }, [navigateToQuotesList]);

  const handleSaveAsDraft = async () => {
    if (currentStep === 2) {
      setIsCreating(true);
      try {
        await handleValidationOrder(false);
      } finally {
        navigateToQuotesList();
        setIsCreating(false);
      }
      return;
    }
    if (currentStep === 4) {
      if (OrderWorkunitsSummaryRef.current) {
        setIsCreating(true);
        try {
          await OrderWorkunitsSummaryRef.current.saveAsDraft();
        } finally {
          navigateToQuotesList();
          setIsCreating(false);
        }
      }
      return;
    }
    if (currentStep === 3) {
      setIsCreating(true);
      try {
        await OrderWorkunitsAffectationRef.current?.validateOrderStatus();
      } finally {
        navigateToQuotesList();
        setIsCreating(false);
      }
      return;
    }
    if (currentStep === 1) {
      setIsCreating(true);
      try {
        await EngagementParametersRef.current?.handleSubmitEngagementPara();
      } finally {
        navigateToQuotesList();
        setIsCreating(false);
      }
    }
  };

  // Validation
  useEffect(() => {
    // sourceBaseOrder only updates on fetch/save; EngagementParameters mirrors every
    // field edit into state.catalogToOrder.order (injectOrderAction) as the user types, so
    // that's what must be checked here - otherwise clearing name_spec/dates in the form (or
    // leaving them empty while creating a brand-new order) wouldn't disable Save & Next
    // until after a save round-trip. Scopes/Delivery Managers/Consultants are tracked in
    // this component's own state regardless of whether the order exists yet.
    const currentOrder = liveEditOrder?.id === sourceBaseOrder?.id ? liveEditOrder : sourceBaseOrder;
    const hasMissingDateOrName =
      moment(currentOrder?.end_date).isBefore(currentOrder?.start_date) ||
      !currentOrder?.end_date ||
      !currentOrder?.start_date ||
      !currentOrder?.name_spec ||
      currentOrder?.name_spec?.length === 0;

    setIsMissingInputs(
      hasMissingDateOrName ||
      selectedScopes.length === 0 ||
      selectedDeliveryManager.length === 0 ||
      selectedConsultants.length === 0
    );
  }, [
    sourceBaseOrder?.id,
    sourceBaseOrder?.end_date,
    sourceBaseOrder?.start_date,
    sourceBaseOrder?.name_spec,
    liveEditOrder?.id,
    liveEditOrder?.end_date,
    liveEditOrder?.start_date,
    liveEditOrder?.name_spec,
    selectedScopes,
    selectedDeliveryManager,
    selectedConsultants,
  ]);

  // Workunits step needs a valid catalog_id/order_id even when the order was just
  // created in step 1 and isn't reflected in the URL yet (route has no :order_id).
  const workunitsMatch = useMemo(
    () => ({
      params: {
        catalog_id: catalog_id || String(sourceBaseOrder?.catalog_id ?? ''),
        order_id: effectiveOrderId,
      },
    }),
    [catalog_id, sourceBaseOrder?.catalog_id, effectiveOrderId]
  );

  // Affectation step (case 3) only needs the order_id.
  const affectationMatch = useMemo(
    () => ({
      params: {
        order_id: effectiveOrderId,
      },
    }),
    [effectiveOrderId]
  );

  // Memoized step content
  const renderStepContent = useCallback(() => {
    switch (currentStep) {
      case 1:
        return (
          <EngagementParameters
            ref={EngagementParametersRef}
            selectedConsultants={selectedConsultants}
            setSelectedConsultants={setSelectedConsultants}
            deliveryManagers={deliveryManagers}
            setDeliveryManagers={setDeliveryManagers}
            selectedDeliveryManager={selectedDeliveryManager}
            setSelectedDeliveryManager={setSelectedDeliveryManager}
            representatives={representatives}
            setRepresentatives={setRepresentatives}
            selectedRepresentative={selectedRepresentative}
            setSelectedRepresentative={setSelectedRepresentative}
            selectedScopes={selectedScopes}
            setSelectedScopes={setSelectedScopes}
            isCreating={isCreating}
            setIsCreating={setIsCreating}
            currentStep={currentStep}
            setActiveBaseFlag={setActiveBaseFlag}
            handleNext={handleNext}
            handleCancel={handleCancel}
            onOrderCreated={handleOrderCreated}
            availableScopes={availableScopes}
            availableDeliveryManagers={availableDeliveryManagers}
            availableConsultants={availableConsultants}
            customerId={appState?.customer?.id ?? 0}
            parent_catalog_id={catalog_id ?? String(sourceBaseOrder?.catalog_id ?? '')}
            sourceBaseOrder={sourceBaseOrder}
          />
        );
      case 2:
        return <OrderWorkunits match={workunitsMatch} embedded />;
      case 3:
        return (
          <OrderWorkunitAffectationProvider>
            <OrderWorkunitsAffectation
              ref={OrderWorkunitsAffectationRef}
              match={affectationMatch}
              embedded
              onSavingStateChange={setIsAffectationSaving}
            />
          </OrderWorkunitAffectationProvider>
        );
      case 4:
        return <OrderWorkunitsSummary ref={OrderWorkunitsSummaryRef} match={affectationMatch} embedded />;
      default:
        return null;
    }
  }, [
    currentStep,
    selectedConsultants,
    deliveryManagers,
    availableDeliveryManagers,
    availableScopes,
    availableConsultants,
    selectedDeliveryManager,
    selectedRepresentative,
    representatives,
    selectedScopes,
    isCreating,
    workunitsMatch,
    affectationMatch,
    handleOrderCreated,
    sourceBaseOrder,
    catalog_id,
    appState?.customer?.id,
    handleNext,
    handleCancel,
  ]);

  // Workunits step (case 2) fetches its data in OrderWorkunits' own effect
  // (fetchWorkunitsInCatalog / getPurchaseOrder / getOrderScopes); keep Save
  // and Save as draft disabled until those responses land.
  const isWorkunitsLoading = useMemo(
    () =>
      currentStep === 2 &&
      ((loadings.fetchWorkunitsInCatalog ?? 0) > 0 ||
        (loadings.getPurchaseOrder ?? 0) > 0 ||
        (loadings.getOrderScopes ?? 0) > 0),
    [currentStep, loadings.fetchWorkunitsInCatalog, loadings.getPurchaseOrder, loadings.getOrderScopes]
  );

  const buttonText = useMemo(() => {
    if (isCreating || isAffectationSaving) return 'Saving...';
    if (isProductionOrder && currentStep >= 2 && currentStep < 4) return t('Next_Tm');
    if (isProductionOrder && currentStep === 4) return t('Base_Line_Done');
    if (currentStep === 4) return t('Base_Line_Done');
    return t('Save_Next');
  }, [isCreating, isAffectationSaving, currentStep, t, isProductionOrder]);

  return (
    <Layout
      name={
        sourceBaseOrder?.id && sourceBaseOrder?.name_spec
          ? `${sourceBaseOrder.id}-${sourceBaseOrder.name_spec}`
          : 'Creating Catalog Order'
      }
      path="/quotes"
    >
      {currentStep !== 5 && (
        <>
          <StepperHeader
            css={{ marginBottom: 5 }}
            steps={steps}
            activeStep={activeStep}
            disable={
              isCreating ||
              isLoading ||
              (currentStep === 1 && isMissingInputs) ||
              isAffectationSaving ||
              isWorkunitsLoading ||
              isStepChanging ||
              (currentStep === 2 && selectedWorkunits.length === 0)
            }
            completed={completed}
            onStepClick={handleStepClick}
          />
          {!isLoading && !isStepChanging ? (
            <>
              {renderStepContent()}

              {/* The Box containing navigation buttons */}
              <Box
                sx={{
                  m: 'auto -30px -30px',
                  p: '30px',
                  backgroundColor: '#FFFFFF',
                  boxShadow: '0px 3px 16px #B2BCD571',
                }}
              >
                <Grid container spacing={2} alignItems="center" justifyContent="space-between" flexWrap='wrap'>
                  <Grid item xs={12} md={4} lg={2}>
                    <Button
                      onClick={() => handleCancel()}
                      disabled={isCreating || isLoading || isAffectationSaving || isStepChanging}
                      color="success"
                      variant="contained"
                    >
                      {t('Cancel')}
                    </Button>
                  </Grid>
                  <Grid item xs={12} md={8} lg={10}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={2}
                      sx={{ justifyContent: 'flex-end', flexWrap: 'wrap' }}
                    >
                      <Button
                        onClick={() => handleSaveAsDraft()}
                        disabled={
                          isCreating ||
                          isLoading ||
                          (currentStep === 1 && isMissingInputs) ||
                          isAffectationSaving ||
                          isWorkunitsLoading ||
                          isProductionOrder ||
                          isStepChanging
                        }
                        color="success"
                        variant="contained"
                        sx={{ width: 150 }}
                      >
                        {isCreating ? 'Saving...' : t('Save_As_Draft_Tm')}
                      </Button>

                      <Button
                        onClick={() => handleBack()}
                        disabled={currentStep === 1 || isCreating || isLoading || isAffectationSaving || isStepChanging}
                        color="success"
                        variant="contained"
                        sx={{ width: 150 }}
                      >
                        {t('Back')}
                      </Button>
                      <Tooltip
                        title={
                          currentStep === 2 && selectedWorkunits.length === 0 ? t('Create_Work_Unit_To_Proceed') : ''
                        }
                      >
                        <span>
                          <Button
                            onClick={() => handleSave()}
                            disabled={
                              isCreating ||
                              isLoading ||
                              (currentStep === 1 && isMissingInputs) ||
                              isAffectationSaving ||
                              isWorkunitsLoading ||
                              isStepChanging ||
                              (currentStep === 2 && selectedWorkunits.length === 0)
                            }
                            color="success"
                            variant="contained"
                            sx={{ width: 150 }}
                          >
                            {buttonText}
                          </Button>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            </>
          ) : (
            /* Loading state Box */
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              Loading order data...
            </Box>
          )}
        </> /* End of the outer Fragment */
      )}
      {currentStep === 2 && (
        <ExtractExcelDialogs
          order={catalogOrder}
          isExtractModalOpen={isExtractModalOpen}
          isReferencesModalOpen={isReferencesModalOpen}
          isExtractLoading={Boolean(isExtractLoading)}
          specRef={specRef}
          setSpecRef={setSpecRef}
          chronoRef={chronoRef}
          setChronoRef={setChronoRef}
          referencesError={referencesError}
          handleExtractModalClose={handleExtractModalClose}
          handleOpenReferencesModal={handleOpenReferencesModal}
          handleReferencesModalClose={handleReferencesModalClose}
          downloadExtractAndRedirectWithReferences={downloadExtractAndRedirectWithReferences}
          redirectToPreview={redirectToPreview}
        />
      )}
      <ManagementDrawers />
    </Layout>
  );
};

export default React.memo(Quotes);
